import type {
  AiUsageEvent,
  AiUsageRange,
  AiUsageSummary,
  ApplicationWithStats,
  MessageTypeWithStats,
  OverviewMetrics,
} from '@owl/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../lib/api';
import { computeAppHealth } from '../../../lib/appHealth';
import { rangeIso, timeAgo } from '../../../lib/time';
import { useLoader } from '../../../lib/useLoader';
import { formatInt, formatSeconds, formatUsd } from '../../charts/AiUsageBlocks';
import { Icon } from '../../icons';
import { MessageTypeChip } from '../../shared';
import type { MobileShellCtx } from '../MobileShellCtx';
import { ActivityBars, HealthRing, formatCompact, formatPct } from '../charts';

export function OverviewPage({ ctx }: { ctx: MobileShellCtx }) {
  return (
    <div className="m-page">
      {ctx.viewMode === 'logs' ? <LogsOverview ctx={ctx} /> : <AiOverview ctx={ctx} />}
    </div>
  );
}

function LogsOverview({ ctx }: { ctx: MobileShellCtx }) {
  const [metrics, setMetrics] = useState<OverviewMetrics | null>(null);
  const [apps, setApps] = useState<ApplicationWithStats[]>([]);
  const [mts, setMts] = useState<MessageTypeWithStats[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Apps the overview is scoped to (empty = all apps in the environment).
  const [appFilterIds, setAppFilterIds] = useState<string[]>([]);

  // The selection belongs to one environment; switching env clears it.
  const environment = ctx.environment;
  useEffect(() => {
    void environment;
    setAppFilterIds([]);
  }, [environment]);

  const appFilterKey = appFilterIds.join(',');

  // biome-ignore lint/correctness/useExhaustiveDependencies: appFilterIds is tracked via appFilterKey
  const load = useCallback(
    async (isCancelled: () => boolean) => {
      setLoading(true);
      setErr(null);
      try {
        const { from, to } = rangeIso(ctx.range);
        const [m, a, t] = await Promise.all([
          api.overview(
            from,
            to,
            ctx.environment,
            appFilterIds.length > 0 ? appFilterIds : undefined,
          ),
          api.listApplications(ctx.environment),
          api.listMessageTypes(),
        ]);
        if (isCancelled()) return;
        setMetrics(m);
        setApps(a.items);
        setMts(t.items);
      } catch (e) {
        if (!isCancelled()) setErr((e as Error).message);
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    [ctx.range, ctx.environment, appFilterKey],
  );

  useLoader(load, ctx.refreshTick);

  const rangeWindow = useMemo(() => rangeIso(ctx.range), [ctx.range]);

  const summary = useMemo(() => {
    if (!metrics) return null;
    const t = metrics.totals;
    const health = Math.round((1 - t.errorRate) * 100);
    return {
      health,
      logs: t.logs,
      errorRate: t.errorRate,
      uptime: t.uptime,
      activeApps: t.applications,
      alertsTriggered: t.alertsTriggered,
    };
  }, [metrics]);

  const topApps = useMemo(() => {
    if (!apps) return [];
    const scoped = appFilterIds.length > 0 ? apps.filter((a) => appFilterIds.includes(a.id)) : apps;
    return [...scoped]
      .map((a) => ({ ...a, _health: computeAppHealth(a) }))
      .sort((a, b) => (b.logs24h ?? 0) - (a.logs24h ?? 0))
      .slice(0, 5);
  }, [apps, appFilterIds]);

  if (loading && !metrics) return <div className="m-loading">Cargando…</div>;
  if (err) return <div className="m-error-banner">{err}</div>;
  if (!metrics || !summary) return null;

  const recentCriticals = metrics.recentCriticalEvents.slice(0, 3);
  const recentAlerts = metrics.recentAlerts.slice(0, 3);
  const firstCritical = recentCriticals[0];

  const toggleApp = (id: string) =>
    setAppFilterIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <>
      {apps.length > 1 && (
        <div className="m-app-filter">
          <button
            type="button"
            className={`m-range-chip ${appFilterIds.length === 0 ? 'active' : ''}`}
            onClick={() => setAppFilterIds([])}
          >
            Todas
          </button>
          {apps.map((a) => (
            <button
              type="button"
              key={a.id}
              className={`m-range-chip ${appFilterIds.includes(a.id) ? 'active' : ''}`}
              onClick={() => toggleApp(a.id)}
            >
              {a.name}
            </button>
          ))}
        </div>
      )}

      <section className="health-hero">
        <div className="health-hero-row">
          <div className="health-ring-wrap">
            <HealthRing value={summary.health} size={88} stroke={8} />
            <div className="health-ring-num">
              {summary.health}
              <small>%</small>
            </div>
          </div>
          <div className="health-hero-info">
            <div className="health-hero-label">Estado general</div>
            <div className="health-hero-title">
              {summary.health >= 95
                ? 'Todo '
                : summary.health >= 85
                  ? 'Casi todo '
                  : 'Con incidencias'}
              {summary.health >= 85 && <span className="serif-i">en orden</span>}
            </div>
            <div className="health-hero-sub">
              <span className="ok">●</span> uptime {formatPct(summary.uptime)}
              <span style={{ color: 'var(--fg-4)' }}>·</span>
              <span className="err">●</span> err {formatPct(summary.errorRate)}
            </div>
          </div>
        </div>
        <div className="health-hero-strip">
          <div className="health-hero-cell">
            <div className="v">{summary.activeApps}</div>
            <div className="l">Apps</div>
          </div>
          <div className="health-hero-cell">
            <div className="v">{formatCompact(summary.logs)}</div>
            <div className="l">Logs · {ctx.range}</div>
          </div>
          <div className="health-hero-cell">
            <div
              className="v"
              style={{ color: summary.alertsTriggered > 0 ? '#ff8b8b' : undefined }}
            >
              {summary.alertsTriggered}
            </div>
            <div className="l">Alertas</div>
          </div>
        </div>
      </section>

      {firstCritical && (
        <button
          type="button"
          onClick={() => ctx.openSheet({ kind: 'log', logId: firstCritical.id })}
          style={{ all: 'unset', display: 'block', cursor: 'pointer' }}
        >
          <div className="push-card">
            <div className="ico">
              <Icon.Alert size={18} />
            </div>
            <div className="body">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MessageTypeChip mt={firstCritical.messageType} dense />
                <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-4)' }}>
                  {timeAgo(firstCritical.timestamp)}
                </span>
              </div>
              <div className="title" style={{ marginTop: 6 }}>
                {firstCritical.message}
              </div>
              <div className="sub">{firstCritical.application.name}</div>
            </div>
          </div>
        </button>
      )}

      <section className="m-card">
        <div className="m-card-h">
          <div className="m-card-t">Actividad</div>
          <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-4)' }}>
            {ctx.range}
          </div>
        </div>
        <div className="m-card-body" style={{ paddingTop: 0 }}>
          <ActivityBars
            timeline={metrics.timeline}
            messageTypes={mts}
            from={rangeWindow.from}
            to={rangeWindow.to}
            onBucketSelect={({ fromMs, size }) =>
              ctx.openSheet({
                kind: 'bucketLogs',
                fromMs,
                size,
                applicationIds: appFilterIds.length > 0 ? appFilterIds : undefined,
              })
            }
          />
        </div>
      </section>

      <div className="m-section-h">
        <span className="m-section-t">Top apps</span>
        <button
          type="button"
          className="m-section-action"
          onClick={() => ctx.navigate({ name: 'apps' })}
        >
          Ver todas <Icon.ChevronRight size={11} />
        </button>
      </div>
      <section className="m-card">
        {topApps.length === 0 && <div className="m-empty">No hay apps</div>}
        {topApps.map((a) => {
          const ratio = 1 - (a.errorRate24h ?? 0);
          const pct = Math.round(ratio * 100);
          const cls = pct >= 95 ? '' : pct >= 85 ? 'warn' : 'err';
          return (
            <button
              type="button"
              key={a.id}
              className="app-row"
              onClick={() => ctx.navigate({ name: 'appdash', applicationId: a.id, tab: 'logs' })}
            >
              <div className="app-avatar">{a.product.slice(0, 2)}</div>
              <div className="info">
                <div className="name">
                  <span className={`dot health-${a._health}`} />
                  {a.name}
                </div>
                <div className="meta">
                  <span>{formatCompact(a.logs24h)} logs/24h</span>
                  <span className="dot-sep">·</span>
                  <span>{a.lastLogAt ? timeAgo(a.lastLogAt) : 'sin logs'}</span>
                </div>
              </div>
              <div className="right">
                <span className={`pct ${cls}`}>{pct}%</span>
                <div className={`hbar ${cls}`}>
                  <span style={{ width: `${pct}%` }} />
                </div>
              </div>
            </button>
          );
        })}
      </section>

      <div className="m-section-h">
        <span className="m-section-t">Alertas recientes</span>
        <button
          type="button"
          className="m-section-action"
          onClick={() => ctx.navigate({ name: 'alerts', tab: 'events' })}
        >
          Ver todas <Icon.ChevronRight size={11} />
        </button>
      </div>
      <section className="m-card">
        {recentAlerts.length === 0 && <div className="m-empty">Sin alertas recientes</div>}
        {recentAlerts.map((ev) => (
          <button
            type="button"
            key={ev.id}
            className="alert-event"
            onClick={() => ctx.openSheet({ kind: 'alertEvent', eventId: ev.id })}
          >
            <div style={{ background: 'rgba(245,158,11,0.6)' }} />
            <div className="body">
              <div className="top">
                <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>Alert event</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--fg-4)' }}>
                  {timeAgo(ev.triggeredAt)}
                </span>
              </div>
              <div className="name" style={{ marginTop: 6 }}>
                {ev.id.slice(0, 8)}…
              </div>
              <div className="sub">{apps.find((a) => a.id === ev.applicationId)?.name ?? '—'}</div>
            </div>
            <div className="rt">
              <Icon.ChevronRight size={14} />
            </div>
          </button>
        ))}
      </section>

      <div style={{ height: 8 }} />
    </>
  );
}

const AI_RANGE_MAP: Record<MobileShellCtx['range'], AiUsageRange> = {
  '1h': '24h',
  '6h': '24h',
  '24h': '24h',
  '7d': '7d',
  '30d': '30d',
  mtd: 'mtd',
};

function AiOverview({ ctx }: { ctx: MobileShellCtx }) {
  const [summary, setSummary] = useState<AiUsageSummary | null>(null);
  const [events, setEvents] = useState<AiUsageEvent[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const aiRange = AI_RANGE_MAP[ctx.range];

  const load2 = useCallback(
    async (isCancelled: () => boolean) => {
      setLoading(true);
      setErr(null);
      try {
        const [s, e] = await Promise.all([
          api.aiUsageSummary({ range: aiRange, environment: ctx.environment }),
          api.listAiUsageEvents({ range: aiRange, environment: ctx.environment, limit: 20 }),
        ]);
        if (isCancelled()) return;
        setSummary(s);
        setEvents(e.items);
      } catch (ex) {
        if (!isCancelled()) setErr((ex as Error).message);
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    [aiRange, ctx.environment],
  );

  useLoader(load2, ctx.refreshTick);

  const modelIndex = useMemo(() => {
    const map = new Map<string, AiUsageSummary['byModel'][number]>();
    if (summary) for (const m of summary.byModel) map.set(m.model, m);
    return map;
  }, [summary]);

  if (loading && !summary) return <div className="m-loading">Cargando…</div>;
  if (err) return <div className="m-error-banner">{err}</div>;
  if (!summary) return null;

  const t = summary.totals;
  const intPart = Math.floor(t.costUsd);
  const fracPart = (t.costUsd - intPart).toFixed(4).slice(1);

  return (
    <>
      <section className="cost-hero">
        <div className="cost-hero-label">Coste de IA · {aiRange}</div>
        <div className="cost-hero-amount">
          <span className="cur">$</span>
          {intPart.toLocaleString()}
          <span className="frac">{fracPart}</span>
        </div>
        <div className="cost-hero-sub">
          <span>{formatInt(t.events)} eventos</span>
          <span style={{ color: 'var(--fg-4)' }}>·</span>
          <span>{formatInt(t.inputTokens + t.outputTokens)} tokens</span>
        </div>
        <div className="cost-hero-strip">
          <div className="cost-hero-cell">
            <div className="v">{formatInt(t.inputTokens)}</div>
            <div className="l">In tokens</div>
          </div>
          <div className="cost-hero-cell">
            <div className="v">{formatInt(t.outputTokens)}</div>
            <div className="l">Out tokens</div>
          </div>
          <div className="cost-hero-cell">
            <div className="v">{formatSeconds(t.audioSeconds)}</div>
            <div className="l">Audio</div>
          </div>
        </div>
      </section>

      <div className="m-section-h">
        <span className="m-section-t">Top modelos</span>
      </div>
      <section className="m-card">
        {summary.byModel.length === 0 && <div className="m-empty">Sin uso AI</div>}
        {summary.byModel.slice(0, 5).map((m) => {
          const pct = t.costUsd > 0 ? (m.costUsd / t.costUsd) * 100 : 0;
          const unit =
            m.pricingUnit === 'tokens'
              ? 'tokens'
              : m.pricingUnit === 'audio_seconds'
                ? 'audio'
                : 'chars';
          return (
            <div key={m.provider + m.model} className="rank-row" style={{ cursor: 'default' }}>
              <span className="swatch" style={{ background: m.color }} />
              <div className="info">
                <div className="name">{m.displayLabel}</div>
                <div className="meta">
                  <span>{m.provider}</span>
                  <span className="sep">·</span>
                  <span>{unit}</span>
                  <span className="sep">·</span>
                  <span>{pct.toFixed(1)}%</span>
                </div>
              </div>
              <div className="right">
                <div className="cost">{formatUsd(m.costUsd)}</div>
                <div className="pct">{formatInt(m.events)} ev</div>
              </div>
            </div>
          );
        })}
      </section>

      <div className="m-section-h">
        <span className="m-section-t">Eventos recientes</span>
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-4)' }}>
          {formatInt(events.length)} total
        </span>
      </div>
      <section className="m-card">
        {events.length === 0 && <div className="m-empty">Sin eventos</div>}
        {events.slice(0, 10).map((ev, idx) => {
          const model = modelIndex.get(ev.model);
          const unit = model?.pricingUnit;
          return (
            <button
              type="button"
              key={`${ev.timestamp}-${idx}`}
              className="ai-event"
              onClick={() => {
                window.__mobileAiEvents = events;
                ctx.openSheet({ kind: 'aiEvent', eventIdx: idx });
              }}
            >
              <div className="bar" style={{ background: model?.color ?? 'var(--fg-3)' }} />
              <div className="body">
                <div className="top">
                  <span className="model">{model?.displayLabel ?? ev.model}</span>
                  <span className="prov">· {ev.provider}</span>
                  <span className="time">{timeAgo(ev.timestamp)}</span>
                </div>
                <div className="units">
                  {unit === 'tokens' && (
                    <>
                      <span className="unit-chip tokens">tokens</span>
                      <span style={{ marginLeft: 8 }}>
                        {formatInt(ev.inputTokens)} in · {formatInt(ev.outputTokens)} out
                        {ev.cachedInputTokens > 0 && (
                          <> · {formatInt(ev.cachedInputTokens)} cached</>
                        )}
                      </span>
                    </>
                  )}
                  {unit === 'audio_seconds' && (
                    <>
                      <span className="unit-chip audio">audio</span>
                      <span style={{ marginLeft: 8 }}>{formatSeconds(ev.audioSeconds)}</span>
                    </>
                  )}
                  {unit === 'characters' && (
                    <>
                      <span className="unit-chip chars">chars</span>
                      <span style={{ marginLeft: 8 }}>{formatInt(ev.characters)}</span>
                    </>
                  )}
                  {!unit && <span>{ev.applicationName}</span>}
                </div>
              </div>
              <div className="cost">{formatUsd(ev.costUsd ?? 0)}</div>
            </button>
          );
        })}
      </section>

      <div style={{ height: 8 }} />
    </>
  );
}

declare global {
  interface Window {
    __mobileAiEvents?: AiUsageEvent[];
  }
}
